import { CodeDataType } from '@/types/pulse';
import { CodeValueFailure, validateCodeValue } from './codeValues';

/**
 * Public names for the datatypes a respondent attribute can hold, matching the
 * slugs signals use for the same four types.
 */
export const ATTRIBUTE_TYPES = ['text', 'boolean', 'number', 'float'] as const;

export type AttributeType = (typeof ATTRIBUTE_TYPES)[number];

export const DATATYPE_BY_ATTRIBUTE_TYPE: Record<AttributeType, CodeDataType> = {
  text: CodeDataType.TEXT,
  boolean: CodeDataType.BOOLEAN,
  number: CodeDataType.NUMBER,
  float: CodeDataType.FLOAT
};

// Derived from the map above so the two cannot drift.
const ATTRIBUTE_TYPE_BY_DATATYPE: Record<number, AttributeType> =
  Object.fromEntries(
    Object.entries(DATATYPE_BY_ATTRIBUTE_TYPE).map(([type, datatype]) => [
      datatype,
      type as AttributeType
    ])
  );

/** Type name for a stored datatype; anything unrecognised reads as text. */
export const attributeTypeFor = (datatype: number | null): AttributeType =>
  ATTRIBUTE_TYPE_BY_DATATYPE[datatype ?? CodeDataType.TEXT] ?? 'text';

export type RespondentCodeValueCheck =
  | { ok: true; value: string }
  | { ok: false; error: string };

// Wording for the attribute dialogs, which sit next to the input being
// corrected and so name the fix rather than the rule.
//
// `option` is absent because an attribute has no option list to fall outside
// of: `respondent_codetypes` declares a datatype and nothing narrower. Giving
// attributes one means adding a case here, which the compiler will ask for.
const ATTRIBUTE_VALUE_ERRORS: Record<
  Exclude<CodeValueFailure, 'option'>,
  string
> = {
  empty: 'Enter a value',
  boolean: 'Choose Yes or No',
  integer: 'Whole numbers only, like 7 or -2',
  number: 'Numbers only, like 0.75'
};

/**
 * Checks a value against its attribute's type and returns what should be
 * stored, phrased for the dashboard's attribute dialogs.
 *
 * Shared by the dashboard and the public API so a value cannot be recorded one
 * way through one and another way through the other.
 */
export function validateRespondentCodeValue(
  datatype: number,
  raw: string
): RespondentCodeValueCheck {
  // Called without an option list, so `option` is not a refusal this can
  // produce; it falls back to the shared wording rather than going unhandled.
  const check = validateCodeValue(datatype, raw);
  if (check.ok) return check;

  return {
    ok: false,
    error:
      check.reason === 'option'
        ? check.error
        : ATTRIBUTE_VALUE_ERRORS[check.reason]
  };
}
